import { motion } from "framer-motion";
import { Clock, CalendarX, Baby, Timer, Sparkle, ArrowLeft } from "@phosphor-icons/react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { useSeo } from "../hooks/useSeo";

const policies = [
  {
    icon: CalendarX,
    title: "Stornierungen & No-Show",
    content: `Wir haben Verständnis dafür, dass Termine gelegentlich verschoben oder abgesagt werden müssen. Wir bitten jedoch um eine Absage mindestens 24 Stunden im Voraus, damit wir den Termin an andere Kund:innen weitergeben können.

Bei Absagen innerhalb von 24 Stunden kann eine Ausfallgebühr berechnet werden. Bei Nichterscheinen ohne Absage behalten wir uns vor, den Termin vollständig in Rechnung zu stellen.`,
  },
  {
    icon: Clock,
    title: "Zu spät kommen",
    content: `Bitte informiere uns so früh wie möglich, falls du dich verspätest. Bei einer Verspätung von mehr als 15 Minuten kann sich die Behandlungsdauer verkürzen oder eine Terminverschiebung erforderlich sein, um den Ablauf für andere Kund:innen nicht zu beeinträchtigen.

Wir geben stets unser Bestes, dich einzuplanen, ohne die Qualität unserer Arbeit zu kompromittieren.`,
  },
  {
    icon: ArrowLeft,
    title: "Rückzahlung",
    content: `Aufgrund der individuellen Natur unserer Dienstleistungen sind Rückerstattungen ausgeschlossen.

Solltest du mit dem Ergebnis nicht zufrieden sein, kontaktiere uns bitte innerhalb von 7 Tagen. Wir prüfen dein Anliegen und nehmen gegebenenfalls eine Anpassung von Schnitt oder Farbe vor.`,
  },
  {
    icon: Baby,
    title: "Kinder & Gäste",
    content: `Um eine ruhige und entspannte Atmosphäre zu bewahren, bitten wir darum, dass Kinder unter 16 Jahren den Termin nur begleiten, wenn es keine andere Möglichkeit gibt.

Begleitpersonen bitten wir, im Empfangsbereich zu warten, um einen reibungslosen Ablauf im Stylingbereich zu gewährleisten.`,
  },
  {
    icon: Timer,
    title: "Zeitbasierte Dienstleistung",
    content: `Unsere Dienstleistungen sind zeitbasiert. Der tatsächliche Preis richtet sich nach Zeitaufwand, Haarlänge und Haardichte.

Entsteht ein Mehraufwand über die geplante Zeit hinaus, berechnen wir 45 € pro weitere 30 Minuten.`,
  },
  {
    icon: Sparkle,
    title: "Produkt- und Pflegeempfehlung",
    content: `Für die Haltbarkeit und Qualität des Ergebnisses empfehlen wir die individuell abgestimmte Heimpflege.

Für Abweichungen durch nicht empfohlene Produkte übernehmen wir keine Haftung.`,
  },
];

const AGBPage = () => {
  useSeo({ title: "AGB & Salonregeln | JS Blond Salon", description: "Allgemeine Geschäftsbedingungen und Salonregeln des JS Blond Salons in Leingarten bei Heilbronn: Termine, Stornierung, Kinder, Wartezeiten.", path: "/agb" });
  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-white">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="caption block mb-4">Salonregeln</span>
            <h1
              className="text-4xl md:text-5xl font-normal tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Was uns wichtig ist
            </h1>
            <p className="text-lg leading-relaxed font-light text-[#2C2C2C]/70">
              Damit wir allen Kund:innen den bestmöglichen Service bieten können, 
              haben wir einige Hinweise für deinen Termin zusammengestellt. Sie sorgen 
              für einen entspannten Ablauf und ermöglichen es uns, uns voll und ganz 
              auf Qualität, Pflege und Styling zu konzentrieren.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Policies */}
      <section className="section-spacing bg-[#F9F7F2]">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {policies.map((policy, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-8 border border-[#E6E2D6]"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#F9F7F2] flex items-center justify-center flex-shrink-0">
                      <policy.icon size={24} className="text-[#9A8C73]" weight="light" />
                    </div>
                    <h2
                      className="text-xl font-medium pt-2"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {policy.title}
                    </h2>
                  </div>
                  <p className="text-[#2C2C2C]/70 text-sm leading-relaxed whitespace-pre-line">
                    {policy.content}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Thank You Note */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mt-12 text-center"
            >
              <div className="bg-[#D4AF37]/10 p-8 border-l-4 border-[#D4AF37]">
                <h3
                  className="text-xl font-medium mb-3"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Vielen Dank für dein Verständnis und deine Unterstützung
                </h3>
                <p className="text-[#2C2C2C]/70">
                  Durch die Einhaltung dieser Richtlinien hilfst du uns, für alle 
                  Kund:innen und unser Team einen reibungslosen und hochwertigen 
                  Ablauf zu gewährleisten.
                </p>
              </div>
            </motion.div>

            {/* Book CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-12 text-center"
            >
              <a
                href="https://www.phorest.com/salon/jsblondsalonughaftungsbeschrae"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="agb-book-btn"
                className="btn-primary inline-block"
              >
                Termin Buchen
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AGBPage;
